import React, { useEffect, useState, useCallback } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../api/client';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Policy {
  id:             string;
  quoteId:        string;
  policyNumber:   string | null;
  insuredName:    string;
  agencyName:     string | null;
  agentName:      string | null;
  carrier:        string | null;
  effectiveDate:  string | null;
  expirationDate: string | null;
  premiumAmount:  number | null;
  currency:       string;
  status:         string;
  notes:          string | null;
  createdAt:      string;
  _count?:        { tasks: number };
}

interface PaginatedResult<T> { items: T[]; total: number; page: number; pageSize: number; totalPages: number; }

interface AmsResult {
  items:  any[];
  total?: number;
  page:   number;
  limit:  number;
  raw?:   any;
}

const emptyForm = {
  quoteId: '', policyNumber: '', insuredName: '', agencyName: '', agentName: '',
  carrier: '', effectiveDate: '', expirationDate: '', premiumAmount: '' as string | number,
  currency: 'USD', status: 'ACTIVE', notes: '',
};

type FormMode = 'add' | 'edit' | null;

// ─── Icons ────────────────────────────────────────────────────────────────────

const IconPlus     = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const IconSearch   = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>;
const IconEdit     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
const IconTrash    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>;
const IconPolicy   = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/></svg>;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(iso: string | null) { return iso ? new Date(iso).toLocaleDateString() : '—'; }
function fmtCcy(amount: number | null, currency: string) {
  if (amount == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
}

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  ACTIVE:    { bg: '#dcfce7', text: '#166534' },
  EXPIRED:   { bg: '#f1f5f9', text: '#475569' },
  CANCELLED: { bg: '#fee2e2', text: '#991b1b' },
  PENDING:   { bg: '#fef9c3', text: '#854d0e' },
};

// ─── Policy form modal ────────────────────────────────────────────────────────

const PolicyForm: React.FC<{
  mode:     FormMode;
  initial:  typeof emptyForm & { id?: string };
  onSave:   (d: typeof emptyForm & { id?: string }) => Promise<void>;
  onCancel: () => void;
  saving:   boolean;
}> = ({ mode, initial, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(initial);
  useEffect(() => setForm(initial), [initial]);

  const s  = (k: keyof typeof emptyForm, v: string | number) => setForm(f => ({ ...f, [k]: v }));
  const lbl: React.CSSProperties = { fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px', display: 'block' };
  const inp: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: '7px', fontSize: '13px', border: '1px solid #e2e8f0', background: '#f8fafc', color: '#1e293b', outline: 'none', boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: '14px', width: '100%', maxWidth: 620, boxShadow: '0 24px 60px rgba(0,0,0,.18)', overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #f1f5f9' }}>
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#1e293b' }}>{mode === 'add' ? 'Add Policy' : 'Edit Policy'}</h2>
        </div>
        <form onSubmit={e => { e.preventDefault(); onSave({ ...form, id: (initial as any).id }); }}>
          <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '14px', maxHeight: '65vh', overflowY: 'auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={lbl}>Quote ID *</label>
                <input style={inp} required value={form.quoteId} onChange={e => s('quoteId', e.target.value)} placeholder="QT-2024-001" disabled={mode === 'edit'} />
              </div>
              <div>
                <label style={lbl}>Policy Number</label>
                <input style={inp} value={form.policyNumber} onChange={e => s('policyNumber', e.target.value)} placeholder="POL-123456" />
              </div>
            </div>
            <div>
              <label style={lbl}>Insured Name *</label>
              <input style={inp} required value={form.insuredName} onChange={e => s('insuredName', e.target.value)} placeholder="John Smith / Acme Corp" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={lbl}>Agency Name</label>
                <input style={inp} value={form.agencyName} onChange={e => s('agencyName', e.target.value)} placeholder="ABC Insurance Agency" />
              </div>
              <div>
                <label style={lbl}>Agent Name</label>
                <input style={inp} value={form.agentName} onChange={e => s('agentName', e.target.value)} placeholder="Jane Doe" />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={lbl}>Carrier</label>
                <input style={inp} value={form.carrier} onChange={e => s('carrier', e.target.value)} placeholder="State Farm" />
              </div>
              <div>
                <label style={lbl}>Status</label>
                <select style={inp} value={form.status} onChange={e => s('status', e.target.value)}>
                  <option value="ACTIVE">Active</option>
                  <option value="PENDING">Pending</option>
                  <option value="EXPIRED">Expired</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={lbl}>Effective Date</label>
                <input style={inp} type="date" value={form.effectiveDate} onChange={e => s('effectiveDate', e.target.value)} />
              </div>
              <div>
                <label style={lbl}>Expiration Date</label>
                <input style={inp} type="date" value={form.expirationDate} onChange={e => s('expirationDate', e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
              <div>
                <label style={lbl}>Premium Amount</label>
                <input style={inp} type="number" min={0} step="0.01" value={form.premiumAmount} onChange={e => s('premiumAmount', e.target.value)} placeholder="5000" />
              </div>
              <div>
                <label style={lbl}>Currency</label>
                <select style={inp} value={form.currency} onChange={e => s('currency', e.target.value)}>
                  <option>USD</option><option>EUR</option><option>GBP</option><option>CAD</option>
                </select>
              </div>
            </div>
            <div>
              <label style={lbl}>Notes</label>
              <textarea style={{ ...inp, minHeight: 70, resize: 'vertical' }} value={form.notes} onChange={e => s('notes', e.target.value)} placeholder="Additional notes…" />
            </div>
          </div>
          <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button type="button" onClick={onCancel} style={{ padding: '8px 18px', borderRadius: '8px', border: '1px solid #e2e8f0', background: 'transparent', color: '#64748b', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: saving ? '#93c5fd' : '#2563eb', color: '#fff', fontWeight: 600, fontSize: '13px', cursor: saving ? 'default' : 'pointer' }}>
              {saving ? 'Saving…' : mode === 'add' ? 'Add Policy' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

const Policies: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'local' | 'ams'>('local');

  // ── Local DB state ──
  const [result, setResult]       = useState<PaginatedResult<Policy> | null>(null);
  const [loading, setLoading]     = useState(true);
  const [formMode, setFormMode]   = useState<FormMode>(null);
  const [formInitial, setInitial] = useState<typeof emptyForm & { id?: string }>(emptyForm);
  const [saving, setSaving]       = useState(false);
  const [toast, setToast]         = useState<{ msg: string; ok: boolean } | null>(null);
  const [page, setPage]           = useState(1);

  // Local search fields
  const [policyNumber, setPolicyNumber] = useState('');
  const [insuredName,  setInsuredName]  = useState('');
  const [agencyName,   setAgencyName]   = useState('');
  const [status,       setStatus]       = useState('');

  // ── AMS state ──
  const [amsQuery, setAmsQuery]   = useState({ policyNumber: '', insuredName: '', agencyName: '', quoteId: '' });
  const [amsResult, setAmsResult] = useState<AmsResult | null>(null);
  const [amsLoading, setAmsLoading] = useState(false);
  const [amsError,  setAmsError]  = useState<string | null>(null);
  const [amsPage,   setAmsPage]   = useState(1);

  const showToast = (msg: string, ok = true) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 3500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (policyNumber) params.set('policyNumber', policyNumber);
      if (insuredName)  params.set('insuredName',  insuredName);
      if (agencyName)   params.set('agencyName',   agencyName);
      if (status)       params.set('status',       status);
      params.set('page', String(page));
      const data = await apiGet<PaginatedResult<Policy>>(`/api/policies?${params}`);
      setResult(data);
    } catch { showToast('Failed to load policies', false); }
    finally { setLoading(false); }
  }, [policyNumber, insuredName, agencyName, status, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [policyNumber, insuredName, agencyName, status]);

  const handleSave = async (data: typeof emptyForm & { id?: string }) => {
    setSaving(true);
    try {
      const body = {
        ...data,
        premiumAmount:  data.premiumAmount !== '' ? Number(data.premiumAmount) : null,
        effectiveDate:  data.effectiveDate  || null,
        expirationDate: data.expirationDate || null,
      };
      if (formMode === 'add') {
        const created = await apiPost<Policy>('/api/policies', body);
        setResult(r => r ? { ...r, items: [created, ...r.items], total: r.total + 1 } : r);
        showToast('Policy added');
      } else {
        const updated = await apiPut<Policy>(`/api/policies/${data.id}`, body);
        setResult(r => r ? { ...r, items: r.items.map(p => p.id === data.id ? updated : p) } : r);
        showToast('Policy updated');
      }
      setFormMode(null);
    } catch (err: any) { showToast(err?.message || 'Save failed', false); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete policy for "${name}"?`)) return;
    try {
      await apiDelete(`/api/policies/${id}`);
      setResult(r => r ? { ...r, items: r.items.filter(p => p.id !== id), total: r.total - 1 } : r);
      showToast('Policy deleted');
    } catch { showToast('Delete failed', false); }
  };

  const openEdit = (p: Policy) => {
    setInitial({
      id: p.id, quoteId: p.quoteId, policyNumber: p.policyNumber || '',
      insuredName: p.insuredName, agencyName: p.agencyName || '',
      agentName: p.agentName || '', carrier: p.carrier || '',
      effectiveDate: p.effectiveDate ? p.effectiveDate.slice(0, 10) : '',
      expirationDate: p.expirationDate ? p.expirationDate.slice(0, 10) : '',
      premiumAmount: p.premiumAmount ?? '', currency: p.currency,
      status: p.status, notes: p.notes || '',
    });
    setFormMode('edit');
  };

  // ── AMS search ──
  const handleAmsSearch = useCallback(async (pg = amsPage) => {
    const { policyNumber: pn, insuredName: inm, agencyName: an, quoteId: qi } = amsQuery;
    if (!pn && !inm && !an && !qi) { setAmsError('Enter at least one search field'); return; }
    setAmsLoading(true);
    setAmsError(null);
    try {
      const params = new URLSearchParams({ page: String(pg), limit: '15' });
      if (pn)  params.set('policyNumber', pn);
      if (inm) params.set('insuredName',  inm);
      if (an)  params.set('agencyName',   an);
      if (qi)  params.set('quoteId',      qi);
      const data = await apiGet<AmsResult>(`/api/policies/ams/search?${params}`);
      setAmsResult(data);
      setAmsPage(pg);
    } catch (e: any) {
      setAmsError(e?.message || 'AMS search failed');
    } finally {
      setAmsLoading(false);
    }
  }, [amsQuery, amsPage]);

  // Helper: get display value from an AMS record field, trying common key variants
  const amsVal = (row: any, ...keys: string[]): string => {
    for (const k of keys) {
      if (row[k] != null && row[k] !== '') return String(row[k]);
    }
    return '—';
  };

  // ── Shared styles ──
  const tabBtn = (active: boolean): React.CSSProperties => ({
    padding: '8px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer',
    fontWeight: 600, fontSize: '13.5px', transition: 'all .15s',
    background: active ? '#2563eb' : 'transparent',
    color:      active ? '#fff'    : '#64748b',
  });

  const inp: React.CSSProperties = {
    width: '100%', padding: '9px 10px 9px 32px', borderRadius: '8px',
    border: '1px solid #e2e8f0', fontSize: '13px', outline: 'none',
    boxSizing: 'border-box', background: '#fff',
  };

  return (
    <div style={{ padding: '28px 32px', maxWidth: 1200, margin: '0 auto' }}>
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 24, zIndex: 2000, padding: '10px 18px', borderRadius: '10px', background: toast.ok ? '#dcfce7' : '#fee2e2', color: toast.ok ? '#166534' : '#991b1b', fontWeight: 600, fontSize: '13px', boxShadow: '0 4px 16px rgba(0,0,0,.1)' }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: 40, height: 40, borderRadius: '10px', background: 'linear-gradient(135deg, #059669, #0891b2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
            <IconPolicy />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#1e293b' }}>Policies</h1>
            <p style={{ margin: 0, fontSize: '13px', color: '#94a3b8' }}>
              {activeTab === 'local'
                ? (result ? `${result.total} local records` : 'Loading…')
                : (amsResult ? `${amsResult.total ?? amsResult.items.length} AMS records` : 'Search AMS')}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* Tab switcher */}
          <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '10px', padding: '4px', gap: '2px' }}>
            <button style={tabBtn(activeTab === 'local')} onClick={() => setActiveTab('local')}>Local DB</button>
            <button style={tabBtn(activeTab === 'ams')}   onClick={() => setActiveTab('ams')}>AMS Search</button>
          </div>
          {activeTab === 'local' && (
            <button onClick={() => { setInitial(emptyForm); setFormMode('add'); }} style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '9px 18px', borderRadius: '9px', border: 'none', background: '#059669', color: '#fff', fontWeight: 600, fontSize: '13.5px', cursor: 'pointer' }}>
              <IconPlus /> Add Policy
            </button>
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          LOCAL DB TAB
      ═══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'local' && (
        <>
          {/* Search bar */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '10px', marginBottom: '16px' }}>
            {[
              { ph: 'Policy number…', val: policyNumber, set: setPolicyNumber },
              { ph: 'Insured name…',  val: insuredName,  set: setInsuredName },
              { ph: 'Agency name…',   val: agencyName,   set: setAgencyName },
            ].map(({ ph, val, set }, i) => (
              <div key={i} style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }}><IconSearch /></span>
                <input style={inp} placeholder={ph} value={val} onChange={e => set(e.target.value)} />
              </div>
            ))}
            <select value={status} onChange={e => setStatus(e.target.value)} style={{ padding: '9px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', outline: 'none', background: '#fff', cursor: 'pointer' }}>
              <option value="">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="PENDING">Pending</option>
              <option value="EXPIRED">Expired</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>

          {/* Table */}
          <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,.05)' }}>
            {loading ? (
              <div style={{ padding: '48px', textAlign: 'center', color: '#94a3b8' }}>
                <div style={{ width: 28, height: 28, border: '2.5px solid #e2e8f0', borderTopColor: '#059669', borderRadius: '50%', animation: 'spin .7s linear infinite', margin: '0 auto 12px' }} />
                Loading policies…
              </div>
            ) : !result?.items.length ? (
              <div style={{ padding: '64px 32px', textAlign: 'center' }}>
                <div style={{ fontSize: '36px', marginBottom: '12px' }}>📋</div>
                <p style={{ color: '#64748b', fontWeight: 600, marginBottom: '6px' }}>No policies found</p>
                <p style={{ color: '#94a3b8', fontSize: '13px' }}>Add policies to link them with email tasks.</p>
              </div>
            ) : (
              <>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                      {['Quote ID', 'Policy #', 'Insured', 'Agency', 'Carrier', 'Effective', 'Premium', 'Status', 'Tasks', ''].map(h => (
                        <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontSize: '11.5px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.items.map((p, i) => {
                      const sc = STATUS_COLORS[p.status] || STATUS_COLORS.PENDING;
                      return (
                        <tr key={p.id} style={{ borderBottom: i < result.items.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                          <td style={{ padding: '12px 14px', fontWeight: 700, fontSize: '13px', color: '#2563eb' }}>{p.quoteId}</td>
                          <td style={{ padding: '12px 14px', fontSize: '13px', color: '#475569', fontFamily: 'monospace' }}>{p.policyNumber || '—'}</td>
                          <td style={{ padding: '12px 14px' }}>
                            <div style={{ fontWeight: 600, fontSize: '13px', color: '#1e293b' }}>{p.insuredName}</div>
                            {p.agentName && <div style={{ fontSize: '11px', color: '#94a3b8' }}>{p.agentName}</div>}
                          </td>
                          <td style={{ padding: '12px 14px', fontSize: '13px', color: '#64748b' }}>{p.agencyName || '—'}</td>
                          <td style={{ padding: '12px 14px', fontSize: '13px', color: '#64748b' }}>{p.carrier || '—'}</td>
                          <td style={{ padding: '12px 14px', fontSize: '12.5px', color: '#475569', whiteSpace: 'nowrap' }}>
                            {fmt(p.effectiveDate)}
                            {p.expirationDate && <div style={{ fontSize: '11px', color: '#94a3b8' }}>– {fmt(p.expirationDate)}</div>}
                          </td>
                          <td style={{ padding: '12px 14px', fontSize: '13px', color: '#1e293b', whiteSpace: 'nowrap' }}>{fmtCcy(p.premiumAmount, p.currency)}</td>
                          <td style={{ padding: '12px 14px' }}>
                            <span style={{ padding: '3px 8px', borderRadius: '20px', fontSize: '11.5px', fontWeight: 600, background: sc.bg, color: sc.text }}>{p.status}</span>
                          </td>
                          <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                            {p._count?.tasks ? (
                              <span style={{ padding: '2px 8px', borderRadius: '20px', background: '#eff6ff', color: '#2563eb', fontSize: '12px', fontWeight: 700 }}>{p._count.tasks}</span>
                            ) : <span style={{ color: '#cbd5e1', fontSize: '12px' }}>0</span>}
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button onClick={() => openEdit(p)} style={{ padding: '5px 8px', borderRadius: '7px', border: '1px solid #e2e8f0', background: '#f8fafc', color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><IconEdit /></button>
                              <button onClick={() => handleDelete(p.id, p.insuredName)} style={{ padding: '5px 8px', borderRadius: '7px', border: '1px solid #fee2e2', background: '#fff5f5', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><IconTrash /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {result.totalPages > 1 && (
                  <div style={{ padding: '12px 16px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafafa' }}>
                    <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ padding: '5px 14px', borderRadius: '7px', border: '1px solid #e2e8f0', background: '#fff', fontSize: '13px', cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.4 : 1 }}>← Prev</button>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>{page} / {result.totalPages}</span>
                    <button disabled={page === result.totalPages} onClick={() => setPage(p => p + 1)} style={{ padding: '5px 14px', borderRadius: '7px', border: '1px solid #e2e8f0', background: '#fff', fontSize: '13px', cursor: page === result.totalPages ? 'default' : 'pointer', opacity: page === result.totalPages ? 0.4 : 1 }}>Next →</button>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          AMS SEARCH TAB
      ═══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'ams' && (
        <>
          {/* AMS search form */}
          <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px', marginBottom: '16px', boxShadow: '0 1px 4px rgba(0,0,0,.05)' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '14px' }}>
              Search AMS — Agency Management System
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px', marginBottom: '14px' }}>
              {([
                { ph: 'Policy number', key: 'policyNumber' },
                { ph: 'Insured name',  key: 'insuredName'  },
                { ph: 'Agency name',   key: 'agencyName'   },
                { ph: 'Quote ID',      key: 'quoteId'      },
              ] as { ph: string; key: keyof typeof amsQuery }[]).map(({ ph, key }) => (
                <div key={key} style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }}><IconSearch /></span>
                  <input
                    style={inp}
                    placeholder={ph}
                    value={amsQuery[key]}
                    onChange={e => setAmsQuery(q => ({ ...q, [key]: e.target.value }))}
                    onKeyDown={e => e.key === 'Enter' && handleAmsSearch(1)}
                  />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <button
                onClick={() => handleAmsSearch(1)}
                disabled={amsLoading}
                style={{ padding: '9px 24px', borderRadius: '8px', border: 'none', background: amsLoading ? '#93c5fd' : '#2563eb', color: '#fff', fontWeight: 600, fontSize: '13.5px', cursor: amsLoading ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: '7px' }}
              >
                {amsLoading
                  ? <><span style={{ width: 13, height: 13, border: '2px solid rgba(255,255,255,.3)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin .7s linear infinite' }} /> Searching…</>
                  : <><IconSearch /> Search AMS</>}
              </button>
              {amsResult && (
                <button onClick={() => { setAmsResult(null); setAmsQuery({ policyNumber: '', insuredName: '', agencyName: '', quoteId: '' }); }} style={{ padding: '9px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', background: 'transparent', color: '#64748b', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}>
                  Clear
                </button>
              )}
              {amsError && <span style={{ fontSize: '13px', color: '#dc2626', fontWeight: 500 }}>⚠ {amsError}</span>}
            </div>
          </div>

          {/* AMS results */}
          {amsResult && (
            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,.05)' }}>
              {amsResult.items.length === 0 ? (
                <div style={{ padding: '64px 32px', textAlign: 'center' }}>
                  <div style={{ fontSize: '36px', marginBottom: '12px' }}>🔍</div>
                  <p style={{ color: '#64748b', fontWeight: 600 }}>No AMS records found</p>
                  <p style={{ color: '#94a3b8', fontSize: '13px' }}>Try different search criteria</p>
                </div>
              ) : (
                <>
                  <div style={{ padding: '12px 16px', background: '#f0f9ff', borderBottom: '1px solid #bae6fd', fontSize: '12px', color: '#0369a1', display: 'flex', justifyContent: 'space-between' }}>
                    <span>Showing <strong>{amsResult.items.length}</strong> records{amsResult.total != null ? ` of ${amsResult.total} total` : ''} · Page {amsPage}</span>
                    <span style={{ color: '#64748b' }}>Source: AMS</span>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        {['Policy #', 'Quote / Submission', 'Insured', 'Agency', 'Effective', 'Status', 'Carrier'].map(h => (
                          <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontSize: '11.5px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {amsResult.items.map((row, i) => (
                        <tr key={i} style={{ borderBottom: i < amsResult.items.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                          <td style={{ padding: '12px 14px', fontFamily: 'monospace', fontSize: '13px', color: '#1e293b', fontWeight: 600 }}>
                            {amsVal(row, 'policy_number', 'policyNumber', 'policy_no')}
                          </td>
                          <td style={{ padding: '12px 14px', fontSize: '13px', color: '#2563eb', fontWeight: 600 }}>
                            {amsVal(row, 'quote_id', 'quoteId', 'submission_number', 'quote_number', 'submission_id')}
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <div style={{ fontWeight: 600, fontSize: '13px', color: '#1e293b' }}>
                              {amsVal(row, 'insured_name', 'insuredName', 'client_name', 'name')}
                            </div>
                            {amsVal(row, 'agent_name', 'agentName', 'agent') !== '—' && (
                              <div style={{ fontSize: '11px', color: '#94a3b8' }}>{amsVal(row, 'agent_name', 'agentName', 'agent')}</div>
                            )}
                          </td>
                          <td style={{ padding: '12px 14px', fontSize: '13px', color: '#64748b' }}>
                            {amsVal(row, 'agency_name', 'agencyName', 'agency')}
                          </td>
                          <td style={{ padding: '12px 14px', fontSize: '12.5px', color: '#475569', whiteSpace: 'nowrap' }}>
                            {(() => { const v = amsVal(row, 'effective_date', 'effectiveDate', 'eff_date'); return v !== '—' ? new Date(v).toLocaleDateString() : '—'; })()}
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            {(() => {
                              const s = amsVal(row, 'status', 'policy_status', 'submission_status');
                              const upper = s.toUpperCase();
                              const sc = STATUS_COLORS[upper] || { bg: '#f1f5f9', text: '#475569' };
                              return <span style={{ padding: '3px 8px', borderRadius: '20px', fontSize: '11.5px', fontWeight: 600, background: sc.bg, color: sc.text }}>{s}</span>;
                            })()}
                          </td>
                          <td style={{ padding: '12px 14px', fontSize: '13px', color: '#64748b' }}>
                            {amsVal(row, 'carrier', 'carrier_name', 'insurer')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* AMS pagination */}
                  <div style={{ padding: '12px 16px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafafa' }}>
                    <button
                      disabled={amsPage <= 1 || amsLoading}
                      onClick={() => handleAmsSearch(amsPage - 1)}
                      style={{ padding: '5px 14px', borderRadius: '7px', border: '1px solid #e2e8f0', background: '#fff', fontSize: '13px', cursor: amsPage <= 1 ? 'default' : 'pointer', opacity: amsPage <= 1 ? 0.4 : 1 }}
                    >← Prev</button>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>Page {amsPage}</span>
                    <button
                      disabled={amsResult.items.length < 15 || amsLoading}
                      onClick={() => handleAmsSearch(amsPage + 1)}
                      style={{ padding: '5px 14px', borderRadius: '7px', border: '1px solid #e2e8f0', background: '#fff', fontSize: '13px', cursor: amsResult.items.length < 15 ? 'default' : 'pointer', opacity: amsResult.items.length < 15 ? 0.4 : 1 }}
                    >Next →</button>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}

      {formMode && <PolicyForm mode={formMode} initial={formInitial} onSave={handleSave} onCancel={() => setFormMode(null)} saving={saving} />}
    </div>
  );
};

export default Policies;
